// components/layout/Sidebar.jsx
import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  X,
  Container,
  ChevronDown,
  ChevronRight,
  Menu,
  ChevronLeft,
} from "lucide-react";
import { NAVIGATION_ITEMS } from "../../constants";

const Sidebar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [expandedItems, setExpandedItems] = useState(["jobs"]);
  const [isCollapsed, setIsCollapsed] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Close mobile sidebar when resizing to desktop
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) {
        setSidebarOpen(false);
      }
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const toggleExpanded = (itemId) => {
    setExpandedItems((prev) =>
      prev.includes(itemId)
        ? prev.filter((id) => id !== itemId)
        : [...prev, itemId]
    );
  };

  const handleNavigation = (path) => {
    navigate(path);
    if (window.innerWidth < 1024) {
      setSidebarOpen(false);
    }
  };

  const isActiveItem = (item) => {
    if (item.path === location.pathname) return true;
    if (item.subItems) {
      return item.subItems.some(
        (subItem) => subItem.path === location.pathname
      );
    }
    return false;
  };

  const isActiveSubItem = (subItem) => {
    return subItem.path === location.pathname;
  };

  return (
    <>
      {/* Mobile menu button */}
      <button
        className="fixed top-4 left-4 z-30 lg:hidden p-2 rounded-lg bg-blue-800 text-white shadow-lg"
        onClick={() => setSidebarOpen(true)}
      >
        <Menu className="w-6 h-6" />
      </button>

      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <div className="flex">
        <div
          className={`
                fixed lg:static inset-y-0 left-0 z-50 
                ${isCollapsed ? "w-20" : "w-54"}
                transform ${sidebarOpen ? "translate-x-0" : "-translate-x-full"} 
                transition-all duration-300 ease-in-out 
                lg:translate-x-0
                bg-gradient-to-b from-blue-900 via-blue-800 to-blue-900 
                shadow-2xl flex flex-col
            `}
        >
          {/* Logo Section */}
          <div
            className={`
                    flex items-center ${isCollapsed ? "justify-center" : "justify-between"} 
                    h-16 px-6 bg-blue-800/50 backdrop-blur-sm 
                    border-b border-blue-700/50 flex-shrink-0
                `}
          >
            <div
              className={`flex items-center ${isCollapsed ? "justify-center" : "min-w-0"}`}
            >
              <div className="w-10 h-10 bg-gradient-to-r from-blue-400 to-blue-500 rounded-xl flex items-center justify-center shadow-lg shadow-blue-600/30 flex-shrink-0">
                <Container className="w-6 h-6 text-white" />
              </div>
              {!isCollapsed && (
                <div className="ml-3 min-w-0">
                  <h1 className="text-xl font-bold text-white truncate">
                    TrackTrace
                  </h1>
                </div>
              )}
            </div>

            {!isCollapsed && (
              <button
                onClick={() => setSidebarOpen(false)}
                className="lg:hidden text-white hover:text-blue-200 transition-colors p-1 rounded-lg hover:bg-blue-700/30"
                aria-label="Close sidebar"
              >
                <X className="w-6 h-6" />
              </button>
            )}
          </div>

          {/* Navigation Menu */}
          <nav className="flex-1 px-4 py-6 overflow-y-auto">
            <div className="space-y-2">
              {NAVIGATION_ITEMS.map((item) => {
                const IconComponent = item.icon;
                const isActive = isActiveItem(item);
                const hasSubItems = item.subItems && item.subItems.length > 0;
                const isExpanded = expandedItems.includes(item.id);

                return (
                  <div key={item.id}>
                    {/* Main Item */}
                    <button
                      onClick={() => {
                        if (hasSubItems) {
                          toggleExpanded(item.id);
                        } else {
                          handleNavigation(item.path);
                        }
                      }}
                      className={`
                                            w-full flex items-center 
                                            ${isCollapsed ? "justify-center px-3" : "justify-between px-4"} 
                                            py-3 rounded-xl transition-all duration-200 group relative overflow-hidden
                                            ${
                                              isActive
                                                ? "bg-blue-600 text-white shadow-lg shadow-blue-600/30"
                                                : "text-blue-200 hover:bg-blue-700/50 hover:text-white"
                                            }
                                            focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-opacity-50
                                        `}
                      aria-expanded={hasSubItems ? isExpanded : undefined}
                    >
                      <div className="flex items-center min-w-0">
                        {IconComponent && (
                          <IconComponent
                            className={`
                                                    w-5 h-5 transition-transform duration-200 flex-shrink-0
                                                    ${isActive ? "scale-110" : "group-hover:scale-105"}
                                                    ${isCollapsed ? "" : "mr-3"}
                                                `}
                          />
                        )}
                        {!isCollapsed && (
                          <div className="min-w-0 flex-1 text-left">
                            <span className="font-medium block truncate">
                              {item.name}
                            </span>
                          </div>
                        )}
                      </div>

                      {hasSubItems && !isCollapsed && (
                        <div className="transition-transform duration-200 flex-shrink-0 ml-2">
                          {isExpanded ? (
                            <ChevronDown className="w-4 h-4" />
                          ) : (
                            <ChevronRight className="w-4 h-4" />
                          )}
                        </div>
                      )}

                      {/* Active indicator */}
                      {isActive && (
                        <div className="absolute left-0 top-0 bottom-0 w-1 bg-blue-300 rounded-r" />
                      )}
                    </button>

                    {/* Sub Items */}
                    {hasSubItems && isExpanded && !isCollapsed && (
                      <div className="ml-4 mt-2 space-y-1 border-l-2 border-blue-700/30 pl-4">
                        {item.subItems.map((subItem) => {
                          const SubIcon = subItem.icon;
                          const isSubActive = isActiveSubItem(subItem);

                          return (
                            <button
                              key={subItem.id}
                              onClick={() => handleNavigation(subItem.path)}
                              className={`
                                                            w-full flex items-center px-3 py-2 text-left rounded-lg 
                                                            transition-all duration-200 text-sm min-w-0
                                                            ${
                                                              isSubActive
                                                                ? "bg-blue-600/50 text-white"
                                                                : "text-blue-300 hover:bg-blue-700/30 hover:text-white"
                                                            }
                                                            focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-opacity-50
                                                        `}
                            >
                              {SubIcon && (
                                <SubIcon className="w-4 h-4 mr-2 flex-shrink-0" />
                              )}
                              <span className="truncate">{subItem.name}</span>
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </nav>

          {/* Collapse/Expand Button at Bottom */}
          <div className="hidden lg:flex flex-col flex-shrink-0 border-t border-blue-700/30">
            <button
              onClick={() => setIsCollapsed(!isCollapsed)}
              className={`${isCollapsed ? "mx-2 mb-2" : "mx-4 mb-4"} py-3 px-4 rounded-lg bg-blue-700/30 hover:bg-blue-600/50 text-blue-200 hover:text-white transition-all duration-200 flex items-center justify-center space-x-2 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-opacity-50`}
              aria-label={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
              title={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
            >
              {isCollapsed ? (
                <ChevronRight className="w-5 h-5" />
              ) : (
                <>
                  <ChevronLeft className="w-4 h-4" />
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default Sidebar;
